// Shared API + helpers for the Tasks views (Kanban, Sprints, Epic, Graph, Home).
// Reuses Orka's existing /api endpoints — no new backend required for read flows.

import { buildTaskWriteBody } from '../../lib/taskFieldHelpers';

const API = '/api';

async function jget(path) {
  const r = await fetch(`${API}${path}`, { credentials: 'include' });
  if (!r.ok) throw new Error(`${r.status} ${path}`);
  return r.json();
}

async function jsend(path, method, body) {
  const r = await fetch(`${API}${path}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!r.ok) {
    let detail = `HTTP ${r.status}`;
    try { const j = await r.json(); detail = j.detail || JSON.stringify(j); } catch { /* */ }
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  if (r.status === 204) return null;
  return r.json();
}

export const tasksApi = {
  // Tasks
  listTasks: async ({ limit = 1000, hidden = false } = {}) => {
    const data = await jget(`/tasks?limit=${limit}&include_hidden=${hidden}`);
    return Array.isArray(data) ? data : (data.tasks || data.items || []);
  },
  updateTask: (id, body) => jsend(`/tasks/${id}`, 'PUT', buildTaskWriteBody(body)),

  // Big Picture sprints + epics
  listSprints: () => jget('/bigpicture/items?type=SPRINT'),
  listEpics: () => jget('/bigpicture/items?type=EPIC'),
  listGoals: () => jget('/bigpicture/items?type=GOAL'),
  listProjects: () => jget('/bigpicture/items?type=PROJECT'),
  createBpItem: (body) => jsend('/bigpicture/items', 'POST', body),
  updateBpItem: (id, body) => jsend(`/bigpicture/items/${id}`, 'PUT', body),
  deleteBpItem: (id) => jsend(`/bigpicture/items/${id}`, 'DELETE'),

  // People (org members)
  listPeople: async () => {
    try {
      const r = await jget('/org/members');
      return r.members || r.items || (Array.isArray(r) ? r : []);
    } catch { return []; }
  },
};

// Status helpers — canonical keys (post-Feb-2026 cleanup).
// Note: these maps remain keyed on the LEGACY string for back-compat with views that
// still pass `task.task_status`. They mirror entries for canonical keys too so new code
// can pass `task.task_status_key` directly.
//
// Feb 2026 colour migration: STATUS_COLORS + PRIORITY_COLORS now use the canonical
// .orka-state-*-soft / .orka-priority-* classes defined in design-system/tokens.css.
// NEVER add raw Tailwind colour utilities (bg-red-500, text-white, etc.) for priority
// or state — those bypass the vocabulary + dark-mode token layer.
export const STATUS_LIST = ['To Do', 'In Progress', 'Partial', 'Blocked', 'Done'];

export const STATUS_COLORS = {
  // legacy strings
  'To Do':       'orka-state-open-soft',
  'In Progress': 'orka-state-in-progress-soft',
  'Partial':     'orka-state-partial-soft',
  'Blocked':     'orka-state-blocked-soft',
  'Done':        'orka-state-done-soft',
  // canonical keys
  open:       'orka-state-open-soft',
  inProgress: 'orka-state-in-progress-soft',
  partial:    'orka-state-partial-soft',
  blocked:    'orka-state-blocked-soft',
  done:       'orka-state-done-soft',
  archived:   'orka-state-archived-soft',
  cancelled:  'orka-state-cancelled-soft',
  deleted:    'orka-state-deleted-soft',
};

export const PRIORITY_LABELS = {
  '-1': 'P-1', 0: 'P0', 1: 'P1', 2: 'P2', 3: 'P3', 4: 'P4', 5: 'P5',
};

export const PRIORITY_COLORS = {
  '-1': 'orka-priority-emergency',
  0:    'orka-priority-fixed',
  1:    'orka-priority-high',
  2:    'orka-priority-medium',
  3:    'orka-priority-low',
  4:    'orka-priority-later',
  5:    'orka-priority-someday',
};

// Filter / group helpers — read both legacy and canonical fields so we work
// during the cleanup transition without breaking existing UI.
import { getTaskDate, getTaskStatusLegacy, getTaskStatusKey } from '../../lib/taskFieldHelpers';

export const matchesFilter = (task, filter) => {
  if (!filter) return true;
  const { search, work_area_ids, statuses, priorities, person_ids, big_picture_ids, dateFrom, dateTo } = filter;
  if (search) {
    const q = search.toLowerCase();
    const t = (task.task_title || '').toLowerCase();
    const d = (task.task_description || '').toLowerCase();
    if (!t.includes(q) && !d.includes(q)) return false;
  }
  if (work_area_ids && work_area_ids.size > 0 && !work_area_ids.has(task.work_area_id)) return false;
  if (statuses && statuses.size > 0) {
    // Accept either legacy string or canonical key in the filter set.
    const legacy = getTaskStatusLegacy(task);
    const canonical = getTaskStatusKey(task);
    if (!statuses.has(legacy) && !statuses.has(canonical)) return false;
  }
  if (priorities && priorities.size > 0 && !priorities.has(task.task_priority)) return false;
  // Owner / responsible person (task_associated_person_id removed; check responsible + observers).
  if (person_ids && person_ids.size > 0) {
    const inResp = task.responsible_person_id && person_ids.has(task.responsible_person_id);
    const inObs = Array.isArray(task.observers) && task.observers.some((o) => person_ids.has(o));
    if (!inResp && !inObs) return false;
  }
  if (big_picture_ids && big_picture_ids.size > 0 && !big_picture_ids.has(task.big_picture_id)) return false;
  const taskDate = getTaskDate(task);
  if (dateFrom && taskDate && taskDate < dateFrom) return false;
  if (dateTo && taskDate && taskDate > dateTo) return false;
  return true;
};

export const groupTasksBy = (tasks, key) => {
  const groups = {};
  for (const t of tasks) {
    const k = t[key] ?? '__none__';
    if (!groups[k]) groups[k] = [];
    groups[k].push(t);
  }
  return groups;
};
